import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import type { GFETreatment, InHouseGFEContext, InHouseGFEVisitCoverage, PatientGFEDetail, ServiceGFERequirement } from "@/types/gfe";
import { requestData } from "@/api/api-config";

// --- API Functions ---

async function getPatientGFEs(patientId: string): Promise<PatientGFEDetail[] | null> {
  return await requestData<PatientGFEDetail[]>(`gfe/patient/${patientId}`, "get", undefined, false);
}
async function getTreatments(includeArchived: boolean): Promise<GFETreatment[] | null> {
  return await requestData<GFETreatment[]>(`gfe/treatments${includeArchived ? "?include_archived=true" : ""}`, "get", undefined, false);
}
async function createTreatment(name: string): Promise<GFETreatment | null> {
  return await requestData<GFETreatment>("gfe/treatments", "post", {
    json: {
      name
    }
  }, false);
}
async function updateTreatment({
  id,
  ...changes
}: {
  id: string;
  name?: string;
  displayOrder?: number;
  isArchived?: boolean;
}): Promise<GFETreatment | null> {
  return await requestData<GFETreatment>(`gfe/treatments/${id}`, "patch", {
    json: changes
  }, false);
}
export async function getServiceRequirements(serviceId: string): Promise<ServiceGFERequirement[] | null> {
  return await requestData<ServiceGFERequirement[]>(`gfe/service/${serviceId}/requirements`, "get", undefined, false);
}

/** Replace only the service's in-house GFE treatments; SpaKinect and Qualiphy stay as they are. */
export async function putServiceInHouseTreatments(serviceId: string, treatmentIds: string[]): Promise<ServiceGFERequirement[] | null> {
  return await requestData<ServiceGFERequirement[]>(`gfe/service/${serviceId}/in-house-treatments`, "put", {
    json: {
      treatmentIds
    }
  }, false);
}
async function getInHouseContext(eventId: string | null | undefined): Promise<InHouseGFEContext | null> {
  return await requestData<InHouseGFEContext>(`gfe/in-house/context${eventId ? `?event_id=${eventId}` : ""}`, "get", undefined, false);
}
async function getInHouseVisitCoverage(eventId: string, patientId: string): Promise<InHouseGFEVisitCoverage | null> {
  return await requestData<InHouseGFEVisitCoverage>(`gfe/in-house/visit-coverage?event_id=${eventId}&patient_id=${patientId}`, "get", undefined, false);
}

// --- Query Keys ---

export const gfeQueryKeys = {
  all: ["gfe"] as const,
  patientGfes: (patientId: string) => [...gfeQueryKeys.all, "patient", patientId] as const,
  treatments: (includeArchived: boolean) => [...gfeQueryKeys.all, "treatments", includeArchived] as const,
  serviceRequirements: (serviceId: string) => [...gfeQueryKeys.all, "service-requirements", serviceId] as const,
  inHouseContext: (eventId: string | null | undefined) => [...gfeQueryKeys.all, "in-house-context", eventId ?? null] as const,
  inHouseVisitCoverage: (eventId: string, patientId: string) => [...gfeQueryKeys.all, "in-house-visit-coverage", eventId, patientId] as const
};

// --- Query Options ---

export const gfeQueryOptions = {
  patientGfes: (patientId: string) => ({
    queryKey: gfeQueryKeys.patientGfes(patientId),
    queryFn: () => getPatientGFEs(patientId),
    select: (data: PatientGFEDetail[] | null) => data ?? []
  }),
  treatments: (includeArchived = false) => ({
    queryKey: gfeQueryKeys.treatments(includeArchived),
    queryFn: () => getTreatments(includeArchived),
    select: (data: GFETreatment[] | null) => data ?? []
  }),
  serviceRequirements: (serviceId: string) => ({
    queryKey: gfeQueryKeys.serviceRequirements(serviceId),
    queryFn: () => getServiceRequirements(serviceId),
    select: (data: ServiceGFERequirement[] | null) => data ?? []
  }),
  inHouseContext: (eventId: string | null | undefined) => ({
    queryKey: gfeQueryKeys.inHouseContext(eventId),
    queryFn: () => getInHouseContext(eventId)
  }),
  inHouseVisitCoverage: (eventId: string, patientId: string) => ({
    queryKey: gfeQueryKeys.inHouseVisitCoverage(eventId, patientId),
    queryFn: () => getInHouseVisitCoverage(eventId, patientId)
  })
};

// --- Mutations ---

async function toastError(error: unknown, fallback: string) {
  const message = await parseError(error, fallback);
  toast({
    title: "Error",
    description: message,
    variant: "error"
  });
}
export const useCreateGFETreatmentMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: createTreatment,
      onSuccess: _temp,
      onError: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateGFETreatmentMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: updateTreatment,
      onSuccess: _temp3,
      onError: _temp4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
function _temp() {
  return queryClient.invalidateQueries({
    queryKey: [...gfeQueryKeys.all, "treatments"]
  });
}
function _temp2(error) {
  return toastError(error, "Couldn't add the treatment");
}
function _temp3() {
  return queryClient.invalidateQueries({
    queryKey: [...gfeQueryKeys.all, "treatments"]
  });
}
function _temp4(error) {
  return toastError(error, "Couldn't save the treatment");
}